import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  ThemeProvider,
  CssBaseline,
  Fade,
  Grow,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import { 
  Lock, 
  Visibility, 
  VisibilityOff, 
  CheckCircle,
  Error as ErrorIcon,
  Security,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import logo from '../images/colored_logo.PNG';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');

  // Password strength indicator
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: '', color: 'grey', percentage: 0 };
    
    let strength = 0;
    if (pwd.length >= 6) strength++;
    if (pwd.length >= 10) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[!@#$%^&*]/.test(pwd)) strength++;

    const labels = ['', t?.weak || 'Weak', t?.fair || 'Fair', t?.good || 'Good', t?.strong || 'Strong', t?.veryStrong || 'Very Strong'];
    const colors = ['grey', 'error', 'warning', 'info', 'success', 'success'];

    return {
      strength: Math.min(strength, 5),
      label: labels[strength],
      color: colors[strength],
      percentage: (strength / 5) * 100
    };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError(t?.passwordMismatch || 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t?.passwordTooShort || 'Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://dhahabjw.com/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t?.passwordResetSuccess || 'Password reset successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        if (response.status === 400 && data.error.includes('invalid or has expired')) {
          setTokenValid(false);
        }
        setError(data.error || t?.resetFailed || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t?.networkError || 'Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(248, 210, 92, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(248, 210, 92, 0.05) 0%, transparent 50%)
            `,
            animation: 'pulse 10s ease-in-out infinite'
          },
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 }
          }
        }}
      >
        {/* Header */}
        <AppHeader
          language={language}
          setLanguage={setLanguage}
          t={t}
          onLoginClick={() => navigate('/')}
        />

        <Container maxWidth="sm" sx={{ pt: { xs: 12, sm: 15 }, pb: 8, position: 'relative', zIndex: 1 }}>
          <Fade in={true} timeout={800}>
            <Box>

              {tokenValid ? (
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(30, 30, 30, 0.95))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(248, 210, 92, 0.2)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      border: '1px solid rgba(248, 210, 92, 0.4)',
                      boxShadow: '0 8px 32px rgba(248, 210, 92, 0.2)',
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    {/* Title Section */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          boxShadow: '0 8px 24px rgba(248, 210, 92, 0.4)'
                        }}
                      >
                        <Security sx={{ fontSize: 40, color: '#000' }} />
                      </Box>

                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          mb: 1
                        }}
                      >
                        {t?.resetPassword || 'Reset Password'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t?.resetPasswordSubtitle || 'Enter your new password below to secure your account'}
                      </Typography>
                    </Box>

                    {/* Alerts */}
                    {error && (
                      <Fade in={true}>
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mb: 3, 
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            border: '1px solid rgba(244, 67, 54, 0.3)',
                            color: 'error.main'
                          }}
                          icon={<ErrorIcon />}
                          onClose={() => setError('')}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    {success && (
                      <Fade in={true}>
                        <Alert 
                          severity="success" 
                          sx={{ 
                            mb: 3,
                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            color: 'success.main'
                          }}
                          icon={<CheckCircle />}
                        >
                          {success}
                        </Alert>
                      </Fade>
                    )}

                    {/* Form */}
                    <Box component="form" onSubmit={handleSubmit}>
                      <TextField
                        fullWidth
                        label={t?.newPassword || 'New Password'}
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: 'primary.main' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />

                      {/* Password Strength Indicator */}
                      {password && (
                        <Fade in={true}>
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Password Strength
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: `${passwordStrength.color}.main`,
                                  fontWeight: 600 
                                }}
                              >
                                {passwordStrength.label}
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={passwordStrength.percentage}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  bgcolor: `${passwordStrength.color}.main`,
                                }
                              }}
                            />
                          </Box>
                        </Fade>
                      )}

                      <TextField
                        fullWidth
                        label={t?.confirmNewPassword || 'Confirm New Password'}
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={confirmPassword && password !== confirmPassword}
                        helperText={
                          confirmPassword && password !== confirmPassword
                            ? t?.passwordMismatch || 'Passwords do not match'
                            : ''
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: 'primary.main' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />

                      {/* Password Requirements Card */}
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          mb: 3,
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                        }}
                      >
                        <CardContent sx={{ py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            {t?.passwordRequirements || 'Password Requirements:'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: password.length >= 6 ? 'success.main' : 'text.secondary',
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5 
                              }}
                            >
                              {password.length >= 6 ? '✓' : '○'} {t?.minSixCharacters || 'At least 6 characters'}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: /[A-Z]/.test(password) && /[a-z]/.test(password) ? 'success.main' : 'text.secondary',
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5 
                              }}
                            >
                              {/[A-Z]/.test(password) && /[a-z]/.test(password) ? '✓' : '○'} {t?.upperLowerCase || 'Upper and lowercase letters'}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: /\d/.test(password) ? 'success.main' : 'text.secondary',
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5 
                              }}
                            >
                              {/\d/.test(password) ? '✓' : '○'} {t?.includeNumber || 'At least one number'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                        sx={{
                          py: 1.8,
                          fontWeight: 700,
                          textTransform: 'none',
                          background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                          color: '#000',
                          boxShadow: '0 4px 12px rgba(248, 210, 92, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                            boxShadow: '0 6px 20px rgba(248, 210, 92, 0.6)',
                            transform: 'translateY(-2px)'
                          },
                          '&:disabled': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.3)',
                            boxShadow: 'none'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: '#000' }} />
                        ) : (
                          t?.resetPassword || 'Reset Password'
                        )}
                      </Button>

                      <Button
                        fullWidth
                        variant="text"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/')}
                        sx={{ 
                          mt: 2,
                          color: 'text.secondary',
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            color: 'primary.main'
                          }
                        }}
                      >
                        {t?.backToLogin || 'Back to Login'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(30, 30, 30, 0.95))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    textAlign: 'center'
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main', mb: 2 }}>
                      {t?.invalidToken || 'Invalid or Expired Link'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {t?.invalidTokenMessage || 'This password reset link is invalid or has expired. Please request a new one.'}
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate('/')}
                      sx={{
                        py: 1.8,
                        background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                        color: '#000',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                        }
                      }}
                    >
                      {t?.goToHome || 'Go to Home'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Fade>
        </Container>

        {/* Footer */}
        <AppFooter language={language} t={t} />
      </Box>
    </ThemeProvider>
  );
};

export default ResetPassword;
